import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Users, School, ChefHat, Building2, Zap, UtensilsCrossed, CalendarDays, CreditCard, BarChart3, DollarSign, TrendingUp, Printer, AlertTriangle, ClipboardList, Landmark, Brain, Cpu, Globe, Shield } from 'lucide-react';
import HeaderEN from '../components/HeaderEN';
import '../styles/index.css';

const PageEcoPulseEN = () => {
  const navigate = useNavigate();
  useEffect(() => { document.title = "EcoLunch - EcoPulse CC | Command Center"; }, []);

  return (
    <div className="home-container">
      <HeaderEN currentPage="ecopulse" lang="en" />

      {/* Hero EcoPulse */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-gradient"></div>
          <div className="hero-grid"></div>
        </div>
        <div className="hero-content">
          <div className="hero-badge animate-fade-in-up">
            <Brain size={16} />
            <span>Proprietary Artificial Intelligence</span>
          </div>
          <h1 className="hero-title animate-fade-in-up" style={{ animationDelay: '0.1s', color: 'rgba(132, 204, 22, 0.95)', textShadow: '0 0 20px rgba(132, 204, 22, 0.3)', fontSize: 'clamp(1.8rem, 4vw, 2.8rem)' }}>
            Command Center
          </h1>
          <p className="animate-fade-in-up" style={{ animationDelay: '0.15s', fontSize: 'clamp(2.5rem, 6vw, 4.5rem)', fontWeight: 800, color: '#00D4FF', marginBottom: '1rem', letterSpacing: '0.05em', textShadow: '0 0 10px rgba(0, 212, 255, 0.9), 0 0 30px rgba(0, 212, 255, 0.8), 0 0 60px rgba(0, 212, 255, 0.6), 0 0 100px rgba(0, 212, 255, 0.4), 0 0 150px rgba(0, 212, 255, 0.2)' }}>
            EcoPulse CC
          </p>
          <p className="hero-subtitle animate-fade-in-up" style={{ animationDelay: '0.2s', fontSize: '1.2rem', lineHeight: '1.8' }}>
            The conversational Command Center at the heart of the EcoLunch platform
          </p>
          <div className="hero-stats animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <div className="stat-item">
              <div className="stat-number">4</div>
              <div className="stat-label">Intelligent portals</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">AI</div>
              <div className="stat-label">Natural language</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">0</div>
              <div className="stat-label">Complex menus</div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro EcoPulse */}
      <section className="section" data-testid="ecopulse-intro-section">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="ecopulse-section">
            <div className="ecopulse-intro">
              <h2>
                <img src="/logo-ecolunch.png" alt="EcoPulse" className="ecopulse-logo" style={{width: '60px', height: '60px'}} />
                EcoPulse CC
              </h2>
              <p className="ecopulse-tagline">
                No more clicks. With EcoPulse, one sentence is enough to control everything.
              </p>
              <p>
                EcoPulse is the conversational intelligence integrated into every portal of the EcoLunch platform.
                Instead of navigating complex menus, <strong>you write your request in natural language</strong> and EcoPulse understands, executes, and confirms instantly.
              </p>
              <p>
                Modify an order, generate a report, check statistics, create a graph: 
                <strong>everything becomes as simple as sending a message</strong>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits EcoPulse */}
      <section className="section" data-testid="ecopulse-benefits">
        <div className="watermark-pattern lighter"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Why EcoPulse changes everything</h2>
            <p className="section-subtitle">
              AI that eliminates software complexity
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon"><Brain size={32} /></div>
              <h3>Natural language</h3>
              <p>Write your request like you would send a text message. EcoPulse understands context, intent, and executes.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Zap size={32} /></div>
              <h3>Instant execution</h3>
              <p>No navigation, no dropdown menus. One sentence = one action executed and confirmed in real time.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon"><Cpu size={32} /></div>
              <h3>Proprietary AI</h3>
              <p>Technology developed and hosted in Quebec, specially designed for school meal operations.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Portals EcoPulse */}
      <section id="portails" className="section" data-testid="ecopulse-portails">
        <div className="watermark-pattern"></div>
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Five portals, one command</h2>
            <p className="section-subtitle">
              Each actor has their own Command Center adapted to their reality
            </p>
          </div>

          <div className="ecopulse-section">
            <div className="ecopulse-cards" data-testid="ecopulse-cards">

              {/* Parents */}
              <div className="ecopulse-card" data-testid="ecopulse-card-parents">
                <div className="ecopulse-card-header" style={{color: '#3B82F6'}}>
                  <div className="portal-icon">
                    <Users size={35} />
                  </div>
                  <div>
                    <h3>Parents<span className="portal-name">Intelligent Control Center</span></h3>
                  </div>
                </div>
                <div className="ecopulse-categories">
                  <div className="ecopulse-category">
                    <h4><UtensilsCrossed size={14} style={{display:'inline', verticalAlign:'middle'}} /> ORDERS & MENUS</h4>
                    <ul>
                      <li><code>Change Emma's meal to salad Thursday</code></li>
                      <li><code>Cancel Friday for both children</code></li>
                      <li><code>Wednesday menu</code></li>
                    </ul>
                  </div>
                  <div className="ecopulse-category">
                    <h4><CalendarDays size={14} style={{display:'inline', verticalAlign:'middle'}} /> SUBSCRIPTIONS</h4>
                    <ul>
                      <li><code>Put Lucas on 4 days/week starting Monday</code></li>
                      <li><code>Activate random order for Emma</code></li>
                    </ul>
                  </div>
                  <div className="ecopulse-category">
                    <h4><CreditCard size={14} style={{display:'inline', verticalAlign:'middle'}} /> FINANCES</h4>
                    <ul>
                      <li><code>Show me my balance</code></li>
                      <li><code>February receipt</code></li>
                      <li><code>How much did I spend this month?</code></li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Caterers */}
              <div className="ecopulse-card" data-testid="ecopulse-card-caterers">
                <div className="ecopulse-card-header" style={{color: '#F97316'}}>
                  <div className="portal-icon">
                    <ChefHat size={35} />
                  </div>
                  <div>
                    <h3>Caterers<span className="portal-name">Operational Control Center</span></h3>
                  </div>
                </div>
                <div className="ecopulse-categories">
                  <div className="ecopulse-category">
                    <h4><BarChart3 size={14} style={{display:'inline', verticalAlign:'middle'}} /> CONSOLIDATED ORDERS</h4>
                    <ul>
                      <li><code>How many meals tomorrow for all schools?</code></li>
                      <li><code>Notre-Dame orders this week</code></li>
                    </ul>
                  </div>
                  <div className="ecopulse-category">
                    <h4><DollarSign size={14} style={{display:'inline', verticalAlign:'middle'}} /> FINANCIAL & ANALYTICS</h4>
                    <ul>
                      <li><code>How many Sezzle sales this month?</code></li>
                      <li><code>Revenue increase since EcoLunch</code></li>
                      <li><code>Revenue per school this week</code></li>
                    </ul>
                  </div>
                  <div className="ecopulse-category">
                    <h4><TrendingUp size={14} style={{display:'inline', verticalAlign:'middle'}} /> GRAPHS</h4>
                    <ul>
                      <li><code>Graph revenue by school, by month, and by menu type</code></li>
                    </ul>
                  </div>
                  <div className="ecopulse-category">
                    <h4><Printer size={14} style={{display:'inline', verticalAlign:'middle'}} /> PRODUCTION</h4>
                    <ul>
                      <li><code>Launch label printing 8am tomorrow to Friday all schools</code></li>
                      <li><code>Send production report to St-Joseph</code></li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Schools */}
              <div className="ecopulse-card" data-testid="ecopulse-card-schools">
                <div className="ecopulse-card-header" style={{color: '#22C55E'}}>
                  <div className="portal-icon">
                    <School size={35} />
                  </div>
                  <div>
                    <h3>Schools<span className="portal-name">Control Center</span></h3>
                  </div>
                </div>
                <div className="ecopulse-categories">
                  <div className="ecopulse-category">
                    <h4><BarChart3 size={14} style={{display:'inline', verticalAlign:'middle'}} /> TRACKING & PARTICIPATION</h4>
                    <ul>
                      <li><code>How many students ordered today?</code></li>
                      <li><code>3rd grade participation rate</code></li>
                      <li><code>Class with best rate</code></li>
                    </ul>
                  </div>
                  <div className="ecopulse-category">
                    <h4><AlertTriangle size={14} style={{display:'inline', verticalAlign:'middle'}} /> ALERTS</h4>
                    <ul>
                      <li><code>Students allergic to peanuts</code></li>
                      <li><code>Who didn't order this week?</code></li>
                    </ul>
                  </div>
                  <div className="ecopulse-category">
                    <h4><ClipboardList size={14} style={{display:'inline', verticalAlign:'middle'}} /> REPORTS</h4>
                    <ul>
                      <li><code>Attendance report for council</code></li>
                      <li><code>Average satisfaction this month</code></li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Boards */}
              <div className="ecopulse-card" data-testid="ecopulse-card-directions">
                <div className="ecopulse-card-header" style={{color: '#A855F7'}}>
                  <div className="portal-icon">
                    <Building2 size={35} />
                  </div>
                  <div>
                    <h3>Boards<span className="portal-name">Governance Center</span></h3>
                  </div>
                </div>
                <div className="ecopulse-categories">
                  <div className="ecopulse-category">
                    <h4><Landmark size={14} style={{display:'inline', verticalAlign:'middle'}} /> CONSOLIDATED VIEW</h4>
                    <ul>
                      <li><code>Global participation all schools this month</code></li>
                      <li><code>Which school has best rate?</code></li>
                    </ul>
                  </div>
                  <div className="ecopulse-category">
                    <h4><DollarSign size={14} style={{display:'inline', verticalAlign:'middle'}} /> DECISIONAL</h4>
                    <ul>
                      <li><code>Compare costs per meal between caterers</code></li>
                      <li><code>Q2 budget forecasts</code></li>
                      <li><code>Network total cost this year</code></li>
                    </ul>
                  </div>
                  <div className="ecopulse-category">
                    <h4><Landmark size={14} style={{display:'inline', verticalAlign:'middle'}} /> DOCUMENTS</h4>
                    <ul>
                      <li><code>Annual report for commission</code></li>
                      <li><code>Export billing 2024-2025</code></li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* CSS (HQ) */}
              <div className="ecopulse-card" data-testid="ecopulse-card-css">
                <div className="ecopulse-card-header" style={{color: '#E74C3C'}}>
                  <div className="portal-icon">
                    <Landmark size={35} />
                  </div>
                  <div>
                    <h3>CSS (HQ)<span className="portal-name">Global Governance Center</span></h3>
                  </div>
                </div>
                <div className="ecopulse-categories">
                  <div className="ecopulse-category">
                    <h4><Globe size={14} style={{display:'inline', verticalAlign:'middle'}} /> GLOBAL VIEW</h4>
                    <ul>
                      <li><code>Global performance all schools this quarter</code></li>
                      <li><code>Compare nutritional compliance by administration</code></li>
                    </ul>
                  </div>
                  <div className="ecopulse-category">
                    <h4><BarChart3 size={14} style={{display:'inline', verticalAlign:'middle'}} /> ANALYTICS & BI</h4>
                    <ul>
                      <li><code>Consolidated annual report for the board</code></li>
                      <li><code>Network participation trends 3 years</code></li>
                      <li><code>Network sustainability KPIs</code></li>
                    </ul>
                  </div>
                  <div className="ecopulse-category">
                    <h4><Shield size={14} style={{display:'inline', verticalAlign:'middle'}} /> GOVERNANCE</h4>
                    <ul>
                      <li><code>Critical network alerts this week</code></li>
                      <li><code>Compliance audit all administrations</code></li>
                    </ul>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Site Web */}
      <section className="section" style={{paddingTop: '2rem', paddingBottom: '2rem'}}>
        <div className="section-container" style={{textAlign: 'center'}}>
          <a href="https://ecotechsysteme.ca" target="_blank" rel="noopener noreferrer" style={{color: 'var(--color-lime)', fontSize: '1.2rem', fontWeight: 600, textDecoration: 'none', borderBottom: '2px solid rgba(132,204,22,0.3)', paddingBottom: '0.25rem'}}>
            ecotechsysteme.ca
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src="/logo-ecolunch.png" alt="EcoLunch" className="footer-logo-image" />
              </div>
              <p className="footer-tagline">
                The unified platform to modernize school meals in Quebec
              </p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>Solutions</h4>
                <a onClick={() => navigate('/en/caterers')}>For Caterers</a>
                <a onClick={() => navigate('/en/parents')}>For Parents</a>
                <a onClick={() => navigate('/en/schools')}>For Schools</a>
                <a onClick={() => navigate('/en/directions')}>For Boards</a>
                <a onClick={() => navigate('/en/css')}>For CSS</a>
              </div>
              <div className="footer-column">
                <h4>Technologies</h4>
                <a onClick={() => navigate('/en/coparenting')}>Co-Parenting</a>
                <a onClick={() => navigate('/en/ecopulse')}>EcoPulse CC</a>
                <a onClick={() => navigate('/en/ecoloop')}>EcoLoop</a>
                <a onClick={() => navigate('/en/platform')}>Platform</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <div className="footer-legal">
              <span>© 2025 EcoLunch. All rights reserved.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PageEcoPulseEN;
